import React, { useCallback, useState } from "react";

import { Dialog, Grid } from "@material-ui/core";
import { Field, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { Row } from "components/Layout";

import {
  getActiveLivestockAgentDeployment,
  getContextByModalType,
  getDashboardFetchingStatus,
  getOnlineStatus,
} from "selectors";
import { ModalTypes } from "constants/navigation";
import OnlineStatus from "components/OnlineStatus";
import { DialogTitle } from "components/MaterialDialog";

import { SmallFaIcon } from "components/FaIcon";
import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import Input from "components/Input";
import { withNamespace } from "components/Form/FormikControls";
import { Form } from "./Form";
import {
  MobileTransactionalCreateSaleFormValues,
  defaultValues,
} from "./types";
export type { MobileTransactionalCreateSaleFormValues } from "./types";
import {
  MobileTransactionalCreateSaleModalTabs,
  MobileTransactionalCreateSaleModalTabId,
} from "./constants";
import { Actions } from "./Actions";
import { validationSchema } from "./validation";
import { useFieldValue, useToggle } from "hooks";
import { SaleAction } from "actions";

export const saleNamespace = "sale";
export const consignmentNamespace = "consignment";
export const saleLotNamespace = "saleLot";
export const skinLotsNamespace = "skinLots";
export const vendorSplitNamespace = "vendorSplit";
export const buyerSplitNamespace = "buyerSplit";

const SaleTitleWrapper = styled.h4`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

interface SaleTitleHeaderProps {
  onClose: () => void;
}

const SaleTitleHeader = ({ onClose }: SaleTitleHeaderProps) => {
  const [isEditingSaleTitle, toggleIsEditingSaleTitle] = useToggle(false);
  const onlineState = useSelector(getOnlineStatus);
  const isFetchingDashboardData = useSelector(getDashboardFetchingStatus);
  const saleTitle = useFieldValue(withNamespace(saleNamespace, "sale_title"));

  return (
    <DialogTitle onClose={onClose} style={{ marginBottom: "-15px" }}>
      <Row fullWidth flex alignCenter paddingHorizontal={2} maxHeight="42px">
        {isEditingSaleTitle ? (
          <Grid item xs={9}>
            <Field name={withNamespace(saleNamespace, "sale_title")}>
              {({ field }) => (
                <Input
                  {...field}
                  autoFocus
                  required
                  onBlur={toggleIsEditingSaleTitle}
                />
              )}
            </Field>
          </Grid>
        ) : (
          <>
            <Grid item xs={8}>
              <SaleTitleWrapper>{saleTitle}</SaleTitleWrapper>
            </Grid>
            <Grid item xs={1}>
              <SmallFaIcon
                icon={faPencil}
                color="black"
                onClick={toggleIsEditingSaleTitle}
              />
            </Grid>
          </>
        )}
        <Grid item xs={3}>
          <OnlineStatus
            status={onlineState.status}
            pendingChangesCount={onlineState.changesOutboxSize}
            isFetching={isFetchingDashboardData}
            textColor="black"
          />
        </Grid>
      </Row>
    </DialogTitle>
  );
};

export interface MobileTransactionalCreateSaleModalProps {
  onClose: () => void;
}

export const MobileTransactionalCreateSaleModal = ({
  onClose,
}: MobileTransactionalCreateSaleModalProps) => {
  const context = useSelector(
    getContextByModalType(ModalTypes.MobileTransactionalCreateSaleModal),
  );

  const dispatch = useDispatch();

  const { saleType, initialValues: initialValuesProp } = context;

  const [activeTab, setActiveTab] =
    useState<MobileTransactionalCreateSaleModalTabId>(
      MobileTransactionalCreateSaleModalTabs.WHO,
    );

  const deployment = useSelector(getActiveLivestockAgentDeployment);

  const {
    species_id: initialValuesSpeciesId,
    sale_sub_type_id: initialValuesSaleSubTypeId,
  } = initialValuesProp;

  const handleSubmit = useCallback(
    (values: MobileTransactionalCreateSaleFormValues) => {
      dispatch(SaleAction.createMobileTransactionalSale(values));
      onClose();
    },
    [dispatch, onClose],
  );

  if (!deployment) {
    return null;
  }

  const mergedInitialValues: MobileTransactionalCreateSaleFormValues = {
    sale: {
      ...defaultValues.sale,
      date: new Date().toISOString().split("T")[0],
      sale_type: saleType,
      sale_sub_type_id: initialValuesSaleSubTypeId || null,
      species_id: initialValuesSpeciesId || null,
      deployment_ids: [deployment.id],
      deployment_sales: [
        {
          deployment_id: deployment.id,
        },
      ],
    },
    consignment: defaultValues.consignment,
    saleLot: defaultValues.saleLot,
    vendorSplit: null,
    buyerSplit: null,
    skinLots: defaultValues.skinLots,
  };

  return (
    <Dialog fullWidth fullScreen onClose={onClose} open>
      <Formik
        initialValues={mergedInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        <>
          <SaleTitleHeader onClose={onClose} />
          <Form activeTab={activeTab} setActiveTab={setActiveTab} />
          <Actions
            onClose={onClose}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </>
      </Formik>
    </Dialog>
  );
};
